import {
  getCustomerSession,
  mergeCustomerSession,
} from "./applyFlowSession";

/** Screens that render UI under /apply-new/:pagename */
export const APPLY_NEW_RENDERED_SCREENS = new Set([
  "landing",
  "location",
  "details",
  "employment",
  "processing",
  "aaCallback",
  "approved",
  "mobileBreReject",
  "offer",
  "addBankDetails",
  "emandate",
  "processBank",
  "selectBank",
  "connectBank",
  "aaMobile",
  "aaAccount",
  "aaConsent",
  "bankSuccess",
  "referenceDetails",
  "referenceReview",
  "selfieIntro",
  "selfieCapture",
  "leadSubmitting",
  "leadSubmitFail",
  "selfieSuccess",
  "sectionLetter",
  "disbursementProcessing",
  "loanInProgress",
  "activeLoanHome",
  "repayment",
  "demoPayment",
  "paymentSuccess",
  "hold",
  "profile",
  "paymentHistory",
]);

/**
 * Backend last_screen → frontend route segment.
 * Success APIs return last_screen; frontend navigates via this map.
 */
export const JOURNEY_LAST_SCREEN_MAP = {
  location: "location",
  pan: "details",
  details: "details",
  // DigiLocker pending after PAN — stay on details + Confirm (no /aadhaarKyc page)
  aadhaar: "details",
  aadhaarkyc: "details",
  aadhaar_kyc: "details",
  pan_confirm: "details",
  employment: "employment",
  processbank: "processBank",
  process_bank: "processBank",
  processing: "processBank",
  aaaccount: "aaAccount",
  aa_account: "aaAccount",
  aacallback: "aaCallback",
  approved: "approved",
  emandate: "emandate",
  e_mandate: "emandate",
  addbankdetails: "addBankDetails",
  selfieintro: "selfieIntro",
  selfie_intro: "selfieIntro",
  selfiesuccess: "selfieSuccess",
  selfie_success: "selfieSuccess",
  sectionletter: "sectionLetter",
  section_letter: "sectionLetter",
  disbursementprocessing: "disbursementProcessing",
  mobilebrereject: "mobileBreReject",
};

export const resolveJourneyRoute = (lastScreen, fallback = "location") => {
  const key = String(lastScreen || "").trim().toLowerCase();
  if (!key) return fallback;
  if (JOURNEY_LAST_SCREEN_MAP[key]) return JOURNEY_LAST_SCREEN_MAP[key];
  // Preserve already-correct route casing from APPLY_NEW_RENDERED_SCREENS
  for (const screen of APPLY_NEW_RENDERED_SCREENS) {
    if (screen.toLowerCase() === key) return screen;
  }
  return fallback;
};

export const isValidEnquiryId = (value) => {
  if (value == null || value === false) return false;
  const text = String(value).trim();
  return Boolean(text) && text !== "0";
};

/** Pull journey fields from typical API envelope shapes */
export const extractJourneyFields = (payload, fallbackScreen = "") => {
  const root = payload && typeof payload === "object" ? payload : {};
  const data =
    root.data && typeof root.data === "object" && !Array.isArray(root.data)
      ? root.data
      : {};
  const nested =
    data.data && typeof data.data === "object" && !Array.isArray(data.data)
      ? data.data
      : {};

  const lastScreenRaw = String(
    root.last_screen ||
      data.last_screen ||
      nested.last_screen ||
      fallbackScreen ||
      ""
  ).trim();

  const lastStep = String(
    root.last_step || data.last_step || nested.last_step || ""
  ).trim();

  const enquiryId =
    root.enquiry_id ??
    data.enquiry_id ??
    nested.enquiry_id ??
    getCustomerSession()?.enquiry_id;

  const leadId = String(
    root.lead_id ||
      data.lead_id ||
      nested.lead_id ||
      getCustomerSession()?.lead_id ||
      ""
  ).trim();

  const route = resolveJourneyRoute(
    lastScreenRaw || fallbackScreen,
    fallbackScreen || "location"
  );

  return { lastScreenRaw, lastStep, enquiryId, leadId, route };
};

/**
 * Persist journey from API success payload into customer session.
 * Returns resolved frontend route (for navigate).
 */
export const persistJourneyFromApi = (payload, fallbackScreen) => {
  const journey = extractJourneyFields(payload, fallbackScreen);
  const { route, lastStep, enquiryId, leadId } = journey;

  if (isValidEnquiryId(enquiryId)) {
    try {
      localStorage.setItem("earlyEnquiryId", String(enquiryId).trim());
      sessionStorage.setItem(
        "applyFlowEarlyEnquiryId",
        String(enquiryId).trim()
      );
    } catch {
      /* ignore */
    }
  }

  mergeCustomerSession({
    ...(leadId ? { lead_id: leadId } : {}),
    ...(isValidEnquiryId(enquiryId)
      ? { enquiry_id: String(enquiryId).trim() }
      : {}),
    ...(lastStep ? { step: lastStep } : {}),
    last_screen: route,
    saved_fields: {
      ...(getCustomerSession()?.saved_fields || {}),
      last_screen: route,
      ...(isValidEnquiryId(enquiryId)
        ? { enquiry_id: String(enquiryId).trim() }
        : {}),
    },
  });

  return journey;
};

/** After PAN verify: stay on details for Confirm → DigiLocker (never aadhaarKyc page) */
export const resolvePanNextRoute = (lastScreenOrRoute) => {
  const key = String(lastScreenOrRoute || "").trim().toLowerCase();
  if (
    !key ||
    key === "pan" ||
    key === "details" ||
    key === "location" ||
    key === "aadhaar" ||
    key === "aadhaarkyc" ||
    key === "aadhaar_kyc" ||
    key === "pan_confirm"
  ) {
    return "details";
  }
  const route = resolveJourneyRoute(lastScreenOrRoute, "details");
  if (route === "aadhaarKyc") return "details";
  return route;
};

/** True when Check Eligibility done but DigiLocker Aadhaar not finished — reopen Confirm */
export const isPanConfirmPending = (session) => {
  const stored = session || getCustomerSession() || {};
  const saved = stored.saved_fields || {};
  const aadhaarDone =
    saved.aadhaar_verified === "1" ||
    saved.aadhaar_verified === true ||
    Boolean(saved.aadhaar_profile);
  if (aadhaarDone) return false;

  // Confirm only after email step — never after PAN type-verify alone
  const email = String(saved.email || stored.email || "").trim();
  const hasEmail = email.includes("@");
  if (!hasEmail) return false;

  const pendingFlag =
    saved.digilocker_pending === "1" ||
    saved.digilocker_pending === true ||
    stored.digilocker_pending === "1";

  const last = String(
    stored.last_screen || saved.last_screen || ""
  )
    .trim()
    .toLowerCase();

  const lastIsAadhaar =
    last === "aadhaar" ||
    last === "aadhaarkyc" ||
    last === "aadhaar_kyc" ||
    last === "pan_confirm";

  return pendingFlag || lastIsAadhaar;
};
